import { loadProgress } from '../shared/progress';
import { renderMyProgressCard } from './my-progress-card';
import { processStreakOnHomeEnter } from './streak/streak';
import { renderStreakPill } from './components/StreakPill';
import { ensureAcademicTheme } from './ui/theme';
import { renderAccountStrip } from './account-strip';
import { totalDueCount } from './spaced-review';
import { DOORS, MAIN_DOOR_ORDER, doorCard, captureToolButton } from './portal/doors';
import { renderGradeUpgradeBanner } from './grade-upgrade-banner';

/**
 * 學習堡壘(Learn Portal)— 登入後唯一學習首頁(學習先呈現)。
 *
 * 學習端入口整併(boss 選項 2,spec docs/spec/LEARN_IA_CONSOLIDATION.md「★ 定案」):
 * 把舊「8 個相似入口 + 兩個重複首頁(home/learn-portal)」收斂成 **4 個 tap target**:
 *
 *     📖 學     —— 課程 + 英語口說
 *     ✏️ 練     —— 出卷/真題/聽力/數學/變形/模擬全收一門(自動出一張 + 計時模擬)
 *     🔁 複習   —— 我的錯題 + 今日到期 + 聽寫
 *     📷 拍考卷 —— 降一階全寬工具(拍考卷 → 錯題本、拍課本 → 內容庫)
 *
 * 四門 icon / 色相兩兩互異(guardrail #2),副標 ≤12 字孩子詞(guardrail #6)。
 * 視覺 = 學術風格設計系統(src/learn/ui):教科書 / 講義紙感、serif 標題、墨色文字、
 * 門色只當低飽和強調。學習與遊戲分離(CLAUDE.md 紅線):本畫面只呈現學習,遊戲收進
 * 底部「⚔️ 進入遊戲」按鈕。
 *
 * 重用既有元件:renderMyProgressCard / renderStreakPill + processStreakOnHomeEnter /
 * renderAccountStrip / cog menu DOM 樣式。
 */
export interface LearnPortalActions {
  /** 📖 學門(課程 + 口說)。 */
  onLearnDoor: () => void;
  /** ✏️ 練門(出卷/真題/聽力/數學/變形/模擬)。 */
  onPracticeDoor: () => void;
  /** 🔁 複習門(錯題 + 到期 + 聽寫)。 */
  onReviewDoor: () => void;
  /** 📷 拍考卷(統一拍照上傳,extractAuto 自動分類考卷/單字)。 */
  onCapture: () => void;
  onParentDashboard: () => void;
  onSettings: () => void;
  onEnterGame: () => void;
}

export function renderLearnPortal(container: HTMLElement, actions: LearnPortalActions): void {
  ensureAcademicTheme();
  const progress = loadProgress();

  // 複習門副標帶「待複習」線索:未 retried 錯題 + 今日到期(取大者當提示數)。
  const mistakeCount = (progress?.mistakes ?? []).filter((m) => !m.retried).length;
  const dueCount = progress ? totalDueCount(progress) : 0;
  const reviewPending = Math.max(mistakeCount, dueCount);

  const root = document.createElement('div');
  root.className = 'ef-page learn-portal';

  const inner = document.createElement('div');
  inner.className = 'ef-page__inner';
  root.appendChild(inner);

  // ── 標題列:serif 「學習堡壘」+ 右側 streak pill ──
  const header = document.createElement('header');
  header.className = 'ef-page__header';
  const heading = document.createElement('div');
  heading.className = 'ef-page__heading';
  const h1 = document.createElement('h1');
  h1.className = 'ef-title';
  h1.textContent = progress ? `學習堡壘 — ${progress.studentName}` : '學習堡壘';
  const sub = document.createElement('p');
  sub.className = 'ef-subtitle';
  sub.textContent = '要學去「學」· 要做卷去「練」· 要回頭去「複習」';
  heading.append(h1, sub);
  const pillSlot = document.createElement('div');
  pillSlot.id = 'streak-pill-slot';
  pillSlot.style.cssText = 'display:flex;align-items:center;';
  header.append(heading, pillSlot);
  inner.appendChild(header);

  // ── 帳號狀態列(登入就在首頁;雲端未啟用時 no-op)──
  renderAccountStrip(inner);

  // ── 我的學習進度 card（重用既有元件,內部樣式不動)──
  const progressSlot = document.createElement('div');
  progressSlot.id = 'progress-card-slot';
  inner.appendChild(progressSlot);

  // ── 升學年提示 banner slot(boss 2026-06-10:期末考過後提示換四年級課程)──
  // 一次性提示;不該顯示時 renderGradeUpgradeBanner = no-op(不插節點)。
  const gradeBannerSlot = document.createElement('div');
  gradeBannerSlot.id = 'grade-upgrade-banner-slot';
  inner.appendChild(gradeBannerSlot);

  // ── 三主門:學 / 練 / 複習(grid;複習門副標帶待複習數)──
  const grid = document.createElement('div');
  grid.className = 'ef-door-grid learn-portal-doors';
  inner.appendChild(grid);

  const doorAction: Record<string, () => void> = {
    learn: actions.onLearnDoor,
    practice: actions.onPracticeDoor,
    review: actions.onReviewDoor,
  };

  for (const id of MAIN_DOOR_ORDER) {
    const spec = DOORS[id];
    const subOverride =
      id === 'review' && reviewPending > 0 ? `${reviewPending} 題待複習` : undefined;
    const card = doorCard(spec, {
      sub: subOverride, // undefined → doorCard 用 spec 預設副標
      onClick: () => {
        cleanup();
        doorAction[id]();
      },
    });
    grid.appendChild(card);
  }

  // ── 拍考卷:降一階全寬工具鈕(非與三門並列主軸,spec guardrail)──
  const capture = captureToolButton({
    onClick: () => {
      cleanup();
      actions.onCapture();
    },
  });
  capture.style.marginTop = '4px';
  inner.appendChild(capture);

  // ── 底部低調入口:進入遊戲(學習先呈現,遊戲退居其後)──
  const gameBtn = document.createElement('button');
  gameBtn.type = 'button';
  gameBtn.id = 'btn-enter-game';
  gameBtn.className = 'ef-btn ef-btn--secondary learn-portal-game';
  gameBtn.style.cssText = 'align-self:center;margin-top:8px;';
  gameBtn.innerHTML =
    '<span aria-hidden="true" style="font-size:18px;line-height:1;">⚔️</span>' +
    '<span>進入遊戲</span>';
  gameBtn.addEventListener('click', () => {
    cleanup();
    actions.onEnterGame();
  });
  inner.appendChild(gameBtn);

  // ── 家長 cog（固定右上;沿用 home.ts cog 樣式)──
  const cog = document.createElement('button');
  cog.type = 'button';
  cog.className = 'cog-icon';
  cog.textContent = '⚙️';
  cog.style.cssText =
    'position: fixed; top: 16px; right: 16px; background: transparent; border: none; font-size: 28px; cursor: pointer; z-index: 100;';
  cog.addEventListener('click', () => openParentMenu(cog, root, actions));

  /** 換頁前清掉本畫面掛載的節點(root + 固定 cog)。 */
  function cleanup(): void {
    root.remove();
    cog.remove();
  }

  container.appendChild(root);
  container.appendChild(cog);

  // 我的學習進度 card — 重用 home.ts 元件(內部樣式不動)。
  renderMyProgressCard(progressSlot);

  // 升學年提示 banner — 期末考過後提示換四年級;升級後重繪首頁(範圍即時反映 + banner 收起)。
  try {
    renderGradeUpgradeBanner(gradeBannerSlot, () => {
      cleanup();
      renderLearnPortal(container, actions);
    });
  } catch (err) {
    console.warn('[learn-portal] grade-upgrade banner mount failed (non-fatal):', err);
  }

  // streak pill — 沿用 home.ts:tick (idempotent within UTC day) 後取 days 畫 pill。
  if (progress) {
    try {
      const snapshot = processStreakOnHomeEnter(progress);
      renderStreakPill(pillSlot, snapshot.days);
    } catch (err) {
      console.warn('[learn-portal] streak pill mount failed (non-fatal):', err);
      try { renderStreakPill(pillSlot, 0); } catch { /* noop */ }
    }
  } else {
    try { renderStreakPill(pillSlot, 0); } catch { /* noop */ }
  }
}

/**
 * 家長選單 — 家長報表 / 設定。沿用 home.ts openCogMenu 的 DOM/樣式/outside-click
 * 收合模式,callback 換成 LearnPortalActions。拍照上傳已是首頁主入口,不在此選單。
 */
function openParentMenu(anchor: HTMLElement, portalRoot: HTMLElement, actions: LearnPortalActions): void {
  document.querySelector('.cog-menu')?.remove();

  const menu = document.createElement('div');
  menu.className = 'cog-menu';
  menu.innerHTML = `
    <button id="cog-dashboard">家長報表</button>
    <button id="cog-settings">⚙️ 設定</button>
    <button id="cog-cancel">取消</button>
  `;

  menu.querySelector('#cog-dashboard')!.addEventListener('click', () => {
    menu.remove();
    portalRoot.remove();
    anchor.remove();
    actions.onParentDashboard();
  });
  menu.querySelector('#cog-settings')!.addEventListener('click', () => {
    menu.remove();
    portalRoot.remove();
    anchor.remove();
    actions.onSettings();
  });
  menu.querySelector('#cog-cancel')!.addEventListener('click', () => {
    menu.remove();
  });

  const closeOnOutside = (e: MouseEvent) => {
    if (!menu.contains(e.target as Node) && e.target !== anchor) {
      menu.remove();
      document.removeEventListener('click', closeOnOutside, true);
    }
  };
  setTimeout(() => document.addEventListener('click', closeOnOutside, true), 0);

  document.body.appendChild(menu);
}
